import { CoursePackageResponseDTO } from '@/jbi-shared/dto/jtms.dtos';
import { CoursePackageStatus } from '@/jbi-shared/types/jtms.types';
import { clone } from '@/jbi-shared/util/group-level-attributes.util';
import { defaultApiState } from '@/store/types/general.types';
import { AxiosError } from 'axios';
import {
  CourseModuleComponentItem,
  IndexBooleanPayload
} from '../course-module/course-module.types';
import {
  CoursePackageComponent,
  CoursePackageState,
  UpdateCoursePackageStatePayload,
  UpsertCoursePackageResponse,
  ValidationCheckPayload
} from './course-package.types';

export const coursePackageMutations = {
  /** API related mutations */
  UPSERT_COURSE_PACKAGE_LOADING(state: CoursePackageState) {
    state.apiState.upsertCoursePackageApi = {
      ...defaultApiState,
      loading: true
    };
  },
  UPSERT_COURSE_PACKAGE_SUCCESS(
    state: CoursePackageState,
    payload: UpsertCoursePackageResponse
  ) {
    state.apiState.upsertCoursePackageApi = {
      ...defaultApiState,
      success: true
    };

    state.upsertedCoursePackage = payload;
  },
  UPSERT_COURSE_PACKAGE_ERROR(state: CoursePackageState, error: AxiosError) {
    state.apiState.upsertCoursePackageApi = {
      ...defaultApiState,
      error
    };
  },

  DELETE_COURSE_PACKAGE_LOADING(state: CoursePackageState) {
    state.apiState.deleteCoursePackageApi = {
      ...defaultApiState,
      loading: true
    };
  },
  DELETE_COURSE_PACKAGE_SUCCESS(state: CoursePackageState) {
    state.apiState.deleteCoursePackageApi = {
      ...defaultApiState,
      success: true
    };
  },
  DELETE_COURSE_PACKAGE_ERROR(state: CoursePackageState, error: AxiosError) {
    state.apiState.deleteCoursePackageApi = {
      ...defaultApiState,
      error
    };
  },

  /** Non-API related mutations */
  CLEAR_ACTIVE_COURSES_PACKAGES(state: CoursePackageState) {
    state.coursePackageTabState.activeCoursePackages = [];
  },

  ADD_ACTIVE_COURSE_PACKAGE(
    state: CoursePackageState,
    payload: CoursePackageComponent
  ) {
    state.coursePackageTabState.activeCoursePackages.push(payload);
  },

  REMOVE_ACTIVE_COURSE_PACKAGE(state: CoursePackageState, index: number) {
    state.coursePackageTabState.activeCoursePackages.splice(index, 1);
  },

  UPDATE_COURSE_PACKAGE(
    state: CoursePackageState,
    payload: UpdateCoursePackageStatePayload
  ) {
    const { index, data } = payload;
    state.coursePackageTabState.activeCoursePackages[index] = {
      ...state.coursePackageTabState.activeCoursePackages[index],
      ...data
    };
  },

  ASSIGN_EXISTING_COURSE_PACKAGES(
    state: CoursePackageState,
    payload: CoursePackageResponseDTO[]
  ) {
    state.coursePackageTabState.activeCoursePackages = []; // Ensure course package array is empty

    payload.forEach((coursePackage, index) => {
      const convertedCoursePackageComponent: CoursePackageComponent = {
        ...coursePackage,
        label: `Package ${index + 1}`,
        hasFieldError: false,
        hasDuplicationError: false,
        shouldDisabledCourseModuleDropdown: false,
        shouldDisableAmountOrCurrency: false
      };

      state.coursePackageTabState.activeCoursePackages.push(
        convertedCoursePackageComponent
      );
    });
  },

  UPDATE_COURSE_PACKAGE_LABEL(state: CoursePackageState) {
    state.coursePackageTabState.activeCoursePackages.forEach(
      (coursePackage, index) => (coursePackage.label = `Package ${index + 1}`)
    );
  },

  UPDATE_COURSE_PACKAGE_FIELD_ERROR(
    state: CoursePackageState,
    payload: IndexBooleanPayload
  ) {
    const { index, data } = payload;
    state.coursePackageTabState.activeCoursePackages[
      index
    ].hasFieldError = data;
  },

  UPDATE_COURSE_PACKAGE_FORM_ERROR(
    state: CoursePackageState,
    payload: boolean
  ) {
    state.coursePackageTabState.coursePackageFormHasError = payload;
  },

  UPDATE_ALL_COURSE_PACKAGES_COURSE_MODULE_DROPDOWN(
    state: CoursePackageState,
    payload: boolean
  ) {
    state.coursePackageTabState.activeCoursePackages.forEach(
      (coursePackage) =>
        (coursePackage.shouldDisabledCourseModuleDropdown = payload)
    );
  },

  REMOVE_COURSE_MODULE_REFERENCES(
    state: CoursePackageState,
    courseModuleToDeleteId: number
  ) {
    const activeCoursePackages =
      state.coursePackageTabState.activeCoursePackages;

    if (activeCoursePackages.length > 0) {
      activeCoursePackages.forEach((coursePackage) => {
        if (
          coursePackage.courseModules &&
          coursePackage.courseModules.length > 0
        ) {
          coursePackage.courseModules = coursePackage.courseModules.filter(
            (cm) => cm.id !== courseModuleToDeleteId
          );
        }
      });
    }
  },

  VALIDATE_ALL_COURSE_PACKAGES(
    state: CoursePackageState,
    validationPayload: ValidationCheckPayload
  ) {
    const { hasModules, isFreeCourse } = validationPayload;
    // Skip validation if no active packages found
    if (state.coursePackageTabState.activeCoursePackages.length === 0) {
      state.coursePackageTabState.coursePackageFormHasError = false;
      return;
    }

    const hasEmptyFields: boolean = state.coursePackageTabState.activeCoursePackages.some(
      (coursePackage) => {
        const isAmountOrCurrencyEmpty =
          !coursePackage.amount || !coursePackage.currency;

        // Only onsidered an error if hasModules is true AND the array is empty.
        const areModulesRequiredAndEmpty =
          hasModules &&
          (!coursePackage.courseModules ||
            (coursePackage.courseModules &&
              coursePackage.courseModules.length === 0));

        if (isFreeCourse) {
          // Skip amount and currency check if course is free
          return areModulesRequiredAndEmpty;
        }
        return isAmountOrCurrencyEmpty || areModulesRequiredAndEmpty;
      }
    );

    const hasFieldError: boolean = state.coursePackageTabState.activeCoursePackages.some(
      (coursePackage) => coursePackage.hasFieldError
    );

    if (!hasFieldError && !hasEmptyFields) {
      state.coursePackageTabState.coursePackageFormHasError = false;
    }

    state.coursePackageTabState.coursePackageFormHasError =
      hasFieldError || hasEmptyFields;
  },

  VALIDATE_COURSE_MODULE_DROPDOWN_STATE(
    state: CoursePackageState,
    allActiveCourseModules: CourseModuleComponentItem[]
  ) {
    const hasActiveModules =
      allActiveCourseModules && allActiveCourseModules.length > 0;

    // Should be disabled if there are NO active modules.
    const shouldBeDisabled = !hasActiveModules;

    state.coursePackageTabState.activeCoursePackages.forEach(
      (coursePackage) => {
        coursePackage.shouldDisabledCourseModuleDropdown = shouldBeDisabled;
      }
    );
  },

  VALIDATE_DUPLICATE_COURSE_PACKAGES(state: CoursePackageState) {
    const activePackages = state.coursePackageTabState.activeCoursePackages;

    // A map to store the "signature" of each package's module combination and their indices.
    const pkgSignatures = new Map<string, number[]>();

    // Special handling for default course packages
    const isDefaultCoursePackages: boolean = activePackages.some(
      (pkg) => pkg.status === CoursePackageStatus.DEFAULT
    );

    // If a course package is DEFAULT AND is the only item in the active array, skip it
    if (isDefaultCoursePackages && activePackages.length === 1) {
      return;
    }

    activePackages.forEach((coursePackage, index) => {
      if (!coursePackage.courseModules) {
        return;
      }
      // Create a sorted array of the module IDs. Sorting is crucial
      // so that [1, 2] and [2, 1] are treated as the same combination
      const sortedModuleIds = coursePackage.courseModules
        .map((m) => m.id!)
        .sort((a, b) => a - b);

      // Create a unique signature from module IDs, amount AND currency.
      // Example signature: "1-2-5_150.00_AUD"
      const signature = `${sortedModuleIds.join('-')}_${coursePackage.amount}_${
        coursePackage.currency
      }`;

      // If the signature is effectively empty (no modules), it can't be a duplicate.
      if (sortedModuleIds.length === 0) {
        return;
      }

      // Store the index of the package against its signature.
      if (!pkgSignatures.has(signature)) {
        pkgSignatures.set(signature, []);
      }
      pkgSignatures.get(signature)!.push(index);
    });

    let anyDuplicatesFound = false;
    activePackages.forEach((coursePackage) => {
      // If a package has no modules, it can't be a duplicate based on this logic
      if (
        !coursePackage.courseModules ||
        coursePackage.courseModules.length === 0
      ) {
        coursePackage.hasDuplicationError = false;
        return;
      }

      const sortedModuleIds = coursePackage.courseModules
        .map((m) => m.id!)
        .sort((a, b) => a - b);
      const signature = `${sortedModuleIds.join('-')}_${coursePackage.amount}_${
        coursePackage.currency
      }`;

      let hasDuplicationError = false;
      // Check if the map has an entry for this signature with more than one index.
      if (
        pkgSignatures.has(signature) &&
        pkgSignatures.get(signature)!.length > 1
      ) {
        hasDuplicationError = true;
        anyDuplicatesFound = true;
      }

      coursePackage.hasDuplicationError = hasDuplicationError;
    });

    // If any duplicates were found, invalidate form.
    if (anyDuplicatesFound) {
      state.coursePackageTabState.coursePackageFormHasError = true;
    }
  },

  TOGGLE_CURRENCY_AND_AMOUNT_FIELDS(
    state: CoursePackageState,
    shouldDisable: boolean
  ) {
    const cloned = clone(state.coursePackageTabState.activeCoursePackages);
    cloned.forEach((pkg) => {
      pkg.shouldDisableAmountOrCurrency = shouldDisable;
    });

    state.coursePackageTabState.activeCoursePackages = cloned;
  }
};
